import React, { useEffect, useState } from 'react'
import type { PreferencesValues } from '@/components/app/layout/preferences-dialog'
import type { MenubarInputs } from '../types/menubar-inputs'

type UseMenubarDialogsArgs = Pick<
  MenubarInputs,
  'isGuidesDialogOpen' | 'onGuidesDialogOpenChange' | 'preferencesValues' | 'onPreferencesChange'
> & { closeMenu: () => void }

/**
 * Guide settings and Preferences dialogs opened from the menu. Each is controlled by the parent
 * when it passes the value + callback, else held locally. Opening the guides dialog closes the menu.
 */
export function useMenubarDialogs({
  isGuidesDialogOpen: isGuidesDialogOpenProp,
  onGuidesDialogOpenChange,
  preferencesValues: preferencesValuesProp,
  onPreferencesChange,
  closeMenu,
}: UseMenubarDialogsArgs) {
  const [internalPreferences, setInternalPreferences] = useState<PreferencesValues>({
    nudge: '1',
    snapDistance: '12',
  })
  const preferencesValues = preferencesValuesProp ?? internalPreferences
  const setPreferencesValues = onPreferencesChange ?? setInternalPreferences

  const [internalGuidesDialogOpen, setInternalGuidesDialogOpen] = useState(false)
  const [isPreferencesDialogOpen, setIsPreferencesDialogOpen] = useState(false)
  const isGuidesDialogControlled =
    typeof isGuidesDialogOpenProp === 'boolean' && typeof onGuidesDialogOpenChange === 'function'
  const isGuidesDialogOpen = isGuidesDialogControlled
    ? isGuidesDialogOpenProp
    : internalGuidesDialogOpen
  const setIsGuidesDialogOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (!isGuidesDialogControlled) {
        setInternalGuidesDialogOpen(nextOpen)
      }

      onGuidesDialogOpenChange?.(nextOpen)
    },
    [isGuidesDialogControlled, onGuidesDialogOpenChange]
  )

  useEffect(() => {
    if (isGuidesDialogOpen) {
      closeMenu()
    }
  }, [isGuidesDialogOpen, closeMenu])

  const openGuidesDialog = () => {
    closeMenu()
    setIsGuidesDialogOpen(true)
  }

  const openPreferencesDialog = () => {
    closeMenu()
    setIsPreferencesDialogOpen(true)
  }

  return {
    isGuidesDialogOpen,
    setIsGuidesDialogOpen,
    isPreferencesDialogOpen,
    setIsPreferencesDialogOpen,
    preferencesValues,
    setPreferencesValues,
    openGuidesDialog,
    openPreferencesDialog,
  }
}
